import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import SyntaxHighlighter from "react-syntax-highlighter";
import { useImmer } from "use-immer";

import { IgDialog, IgSwitch } from "@intelligems/ig-theme";
import { Box, FormControlLabel, Grid, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";

import { useGetExperiencesList } from "../../api/experiences-list/get-experiences-list";
import { CheckoutScriptGenerator } from "../../api/formatters/checkoutScriptGenerator";
import { personalizationKeys } from "../../api/personalizations/use-dashboard-personalization-or-default";
import { getDialog, IGlobalState, setDialog } from "../../store/slices/global";

const StyledDialog = styled(IgDialog)(({ theme }) => ({
  [`& .MuiPaper-root`]: {
    padding: "34px 16px 25px 16px !important",
  },
  pre: {
    borderRadius: "15px",
    backgroundColor: "#F5F5F5 !important",
  },
}));

export const CheckoutScriptDialog = () => {
  const queryClient = useQueryClient();
  const dialog = useSelector(
    getDialog("checkoutScript")
  ) as IGlobalState["dialogs"]["checkoutScript"];

  const dispatch = useDispatch();
  const campaigns = useGetExperiencesList({ category: "personalization" });

  const initialShowFullScript =
    campaigns.isSuccess && campaigns.data.experiencesList.length > 1;

  const [showFullScript, setShowFullScript] = useImmer(false);
  const [code, setCode] = useImmer("");

  useEffect(() => {
    if (dialog.isOpen) {
      void queryClient.invalidateQueries({
        queryKey: personalizationKeys.all(true),
      });
    }
  }, [dialog.isOpen]);

  useEffect(() => {
    if (campaigns.isSuccess) {
      const generated = CheckoutScriptGenerator.generateSimpleScript(
        CheckoutScriptGenerator.buildCampaigns(campaigns.data.experiencesList),
        showFullScript
      );
      if (generated) setCode(generated);
    }
  }, [
    dialog.isOpen,
    campaigns.isSuccess,
    campaigns.isLoading,
    campaigns.data,
    showFullScript,
  ]);

  const toggleShowFullScript = () => {
    setShowFullScript(!showFullScript);
  };

  const handleClose = () => {
    dispatch(setDialog({ dialog: "checkoutScript", open: false }));
  };

  if (!code) {
    return null;
  }

  const codeBlock = (
    <>
      <Grid alignItems={"left"} container>
        <Grid item sx={{ marginLeft: "25px" }}>
          <FormControlLabel
            control={
              <IgSwitch
                checked={showFullScript}
                color="primary"
                onClick={() => {
                  toggleShowFullScript();
                }}
                sx={{ marginRight: "10px" }}
              />
            }
            label={"Show entire checkout script"}
          />
        </Grid>
      </Grid>
      <Box
        sx={{
          textAlign: "left",
          padding: "0px 10px 0px 10px",
          borderRadius: "15px",
          maxHeight: "480px",
          overflowY: "scroll",
        }}
      >
        {/* @ts-ignore */}
        <SyntaxHighlighter
          language={"ruby"}
          showLineNumbers={true}
          useInlineStyles={true}
          startingLineNumber={showFullScript ? 1 : 338}
        >
          {code}
        </SyntaxHighlighter>
      </Box>
    </>
  );

  const handleCopy = async () => {
    if (code) {
      await navigator.clipboard.writeText(code);
    }
  };

  return (
    <StyledDialog
      title={"Checkout Script"}
      subtitle={
        <span>
          You need to update your Checkout Script for this campaign
          {dialog.props?.isTest ? " test" : ""}!
          <br />
          If you already have an Intelligems Campaigns checkout script live,
          update your existing script using the partial script below. If not,
          paste the full script into a new or existing script.
        </span>
      }
      handleClose={handleClose}
      handleYesClick={() => {
        void handleCopy();
      }}
      rawContent={
        campaigns.isLoading ? <Skeleton height={"500px"} /> : codeBlock
      }
      showDialog={dialog.isOpen}
      yesButtonText={"Copy"}
      loading={false}
      shouldNotClose={true}
      dialogHeight={"820px"}
      dialogContentWidth={"900px"}
      noButtonText={"Close"}
      maxWidth={"xl"}
    />
  );
};
